// Okul ve veli modülü (Faz 7): çocuklar (P43–P49), teslim kodu ve teslim alacak kişiler (P50–P53),
// şoför/rehber teslimi (D19–D21). Çocuk güvenliği kayıtları (biniş/iniş/teslim) değiştirilemez; burada yalnız okunur.
import { z } from 'zod';
import type { BoardingMethod, BoardingType, Direction, TripStatus } from '../enums';
import type { ChildState } from '../state';

/** Teslim alacak kişi yakınlığı (etiketler: PICKUP_RELATION_LABELS). */
export const PICKUP_RELATIONS = ['ANNE', 'BABA', 'ANNEANNE', 'BABAANNE', 'DEDE', 'BAKICI', 'DIGER'] as const;
export type PickupRelation = (typeof PICKUP_RELATIONS)[number];

/** Teslim kodu: 4 hane; 3 yanlış denemede kilitlenir, veli yeni kod alır. */
export const PICKUP_CODE_LENGTH = 4;
export const PICKUP_CODE_MAX_ATTEMPTS = 3;

export interface StaffPublic {
  name: string;
  initials: string;
  role: 'DRIVER' | 'GUIDE';
  /** belgeleri doğrulanmış (süresi geçmiş/reddedilmiş belge yok) */
  verified: boolean;
  /** rehber araçta mı (bugünkü seferde atanmış) */
  onBoard: boolean;
}

export interface ChildVehicle {
  plate: string;
  model: string;
  /** muayene ve sigorta geçerli */
  docsValid: boolean;
}

export interface ChildLeg {
  tripId: string;
  direction: Direction;
  status: TripStatus;
  routeCode: string;
  routeName: string;
  plannedStartAt: string;
  stopName: string | null;
  stopPlannedAt: string | null;
  absent: boolean;
}

export interface PickupPersonItem {
  id: string;
  name: string;
  relation: string;
  relationLabel: string;
  phoneMasked: string;
  photoUrl: string | null;
  authorized: boolean;
  /** bugünkü dönüşte teslim alacak kişi */
  today: boolean;
  createdAt: string;
}

export interface ChildSummary {
  id: string;
  firstName: string;
  lastName: string;
  initials: string;
  className: string | null;
  institution: string;
  photoUrl: string | null;
  state: ChildState;
  stateLabel: string;
  stateAt: string | null;
  outbound: ChildLeg | null;
  inbound: ChildLeg | null;
  pickupToday: PickupPersonItem | null;
}

export interface ChildDetail extends ChildSummary {
  morning: { routeCode: string; routeName: string; stop: string; time: string | null } | null;
  evening: { routeCode: string; routeName: string; stop: string; time: string | null } | null;
  driver: StaffPublic | null;
  guide: StaffPublic | null;
  vehicle: ChildVehicle | null;
  guardians: { name: string; relation: string; primary: boolean; me: boolean }[];
  pickupPersons: number;
  /** NFC bayrağı açıksa kart bilgisi (P42) */
  nfcCard: { cardNo: string; status: 'ACTIVE' | 'LOST' | 'BLOCKED' } | null;
}

export interface ChildStep {
  state: ChildState;
  label: string;
  /** done | current (0..1 ilerleme) | pending */
  status: 'done' | 'current' | 'pending';
  progress: number;
}

export interface ChildLiveStatus {
  childId: string;
  firstName: string;
  direction: Direction;
  state: ChildState;
  stateLabel: string;
  /** "Elisa yolda." · "Elisa okula ulaştı." */
  headline: string;
  /** rozet: "Servise bindi · 07:43" */
  badge: { tone: 'ok' | 'route' | 'warn' | 'neutral'; label: string; icon: 'check' | 'clock' | 'arac' | 'x' };
  /** alt satır: "Okula tahmini varış 08:17" */
  detail: string | null;
  etaAt: string | null;
  steps: ChildStep[];
  trip: {
    id: string;
    status: TripStatus;
    routeCode: string;
    delayMin: number;
    position: { lat: number; lng: number; heading: number } | null;
  } | null;
  driver: StaffPublic | null;
  guide: StaffPublic | null;
  vehicle: ChildVehicle | null;
  deliveredTo: { name: string; relationLabel: string } | null;
  updatedAt: string;
}

export interface ChildTimelineItem {
  at: string;
  title: string;
  sub: string | null;
  state: 'done' | 'current' | 'alert' | 'pending';
}

export interface ChildTimeline {
  date: string;
  direction: Direction;
  summary: { tone: 'ok' | 'warn' | 'info'; title: string; body: string } | null;
  items: ChildTimelineItem[];
  route: { tripId: string; distanceKm: number | null; durationMin: number | null } | null;
}

export const ChildTimelineQuery = z.object({
  direction: z.enum(['OUTBOUND', 'INBOUND']).optional(),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

export interface PickupCodeView {
  tripId: string;
  departureAt: string;
  /** 4 hane — yalnız veliye, istek anında; sunucuda düz metin saklanmaz */
  code: string;
  validUntil: string;
  pickupPerson: PickupPersonItem | null;
  usedAt: string | null;
  locked: boolean;
}

export const PickupPersonInput = z.object({
  name: z.string().trim().min(3, 'Ad soyad yazın.').max(80),
  relation: z.enum(PICKUP_RELATIONS),
  phone: z
    .string()
    .trim()
    .regex(/^\+905\d{9}$/, 'Telefon numarası 5 ile başlayan 10 hane olmalı.'),
  photoFileId: z.string().optional(),
});
export type PickupPersonInput = z.infer<typeof PickupPersonInput>;

export const PickupTodayInput = z.object({ pickupPersonId: z.string() });

// ───────── şoför / rehber (D19–D21) ─────────

export interface HandoverInfo {
  passengerId: string;
  name: string;
  className: string | null;
  stopName: string | null;
  /** veli bugün kimin teslim alacağını seçti mi */
  pickupPerson: { id: string; name: string; relationLabel: string } | null;
  /** yetkili kişiler (veli seçmediyse şoför listeden doğrular) */
  authorized: { id: string; name: string; relationLabel: string }[];
  codeIssued: boolean;
  attemptsLeft: number;
  locked: boolean;
  delivered: { at: string; to: string | null } | null;
}

export const HandoverVerifyInput = z.object({
  passengerId: z.string(),
  code: z.string().regex(/^\d{4}$/, 'Kod 4 haneli olmalı.'),
  clientEventId: z.string().min(8).max(80),
  occurredAt: z.string().datetime().optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
});
export type HandoverVerifyInput = z.infer<typeof HandoverVerifyInput>;

export interface HandoverResult {
  ok: boolean;
  attemptsLeft: number;
  locked: boolean;
  deliveredTo: string | null;
  message: string;
}

/** Çocuğun son olayı (rehber listesi ve zaman çizelgesi için). */
export interface ChildEventRef {
  type: BoardingType;
  method: BoardingMethod;
  at: string;
  stopName: string | null;
}

/** P42 NFC kart (NFC modülü). */
export interface NfcCardView {
  passengerId: string;
  holder: string;
  institution: string;
  cardNo: string;
  status: 'ACTIVE' | 'LOST' | 'BLOCKED';
  issuedAt: string;
  lastUsedAt: string | null;
}
